import "./ProjectDetailsStep.css";

import { ArrowRoundRightLightIcon, DownloadDarkIcon, UploadLightIcon } from "../../../../../assets";
import {
  BulkUploadModal,
  Button,
  Card,
  Column,
  ErrorBox,
  InformationBox,
  LandholdingDetailsFields,
  ManualProjectEntryForm,
  ParsedProjectsTable,
  ProjectEntryMethodToggle,
  RadioButtonGroup,
  Row,
  SwitchingDataEntryWarningModal,
} from "../../../../../widget";
import { useProjectDetailsStep } from "./useProjectDetailsStep";

const LANDHOLDING_RADIO_DATA = [
  { key: "yes", value: "Yes" },
  { key: "no", value: "No" },
];

const LANDHOLDING_QUESTION =
  "Are you setting up more than one project on this land, possibly with different project types or standards? This is called a landholding.";

export const ProjectDetailsStep = (): JSX.Element => {
  const {
    variant,
    corners,
    entryMethodResetKey,
    entryMethod,
    isLandholding,
    rows,
    uploadRowErrors,
    landholding,
    formErrors,
    rowErrors,
    landholdingErrors,
    showSwitchModal,
    showUploadModal,
    isParsing,
    isSubmitting,
    isLocked,
    isPartiallyCreated,
    isReadOnly,
    nextLabel,
    submittingMessage,
    parseError,
    showOutOfDateTemplateHint,
    switchProjectCount,
    optionsLoadFailed,
    verifierOptions,
    countryOptions,
    getStandardOptions,
    getProjectTypeOptions,
    handleEntryMethodChange,
    handleConfirmDiscard,
    handleKeepEntries,
    handleLandholdingChoiceChange,
    handleLandholdingChange,
    handleRowChange,
    handleAddProject,
    handleRemoveProject,
    handleDownloadTemplate,
    handleFileSelected,
    handleUpload,
    handleOpenUploadModal,
    handleCancelUpload,
    handleRetryOptions,
    handleNext,
  } = useProjectDetailsStep();

  const isUpload = entryMethod === "upload";
  const isAwaitingUpload = isUpload && rows.length === 0;

  return (
    <div className="ProjectDetailsStep">
      {optionsLoadFailed && (
        <Row spacingV="ml">
          <Column span={12}>
            <ErrorBox
              title="We couldn't load the standards, verifiers and countries"
              text="Nothing on this step can be completed until they load. Try again, and if it keeps failing come back shortly."
            />
            <div className="ProjectDetailsStep_retry">
              <Button variant="secondary" text="Try again" corners={corners} onClick={handleRetryOptions} />
            </div>
          </Column>
        </Row>
      )}

      {isLocked && (
        <Row spacingV="ml">
          <Column span={12}>
            <InformationBox variant="info" title="These projects have already been created">
              <p className="body2">
                They were saved when you last pressed Save, so they can no longer be changed here. Press Next to return
                to Organisations &amp; access and send the invitations again.
              </p>
            </InformationBox>
          </Column>
        </Row>
      )}

      {/* The hook has already left out anything the parsed table shows against its own row. */}
      {!optionsLoadFailed && formErrors.length > 0 && (
        <Row spacingV="ml">
          <Column span={12}>
            <ErrorBox
              title="Please solve the below issues before continuing"
              text={formErrors.map((error) => error.message)}
            />
          </Column>
        </Row>
      )}

      <Row spacingV="ml">
        <Column span={12}>
          <Card corners={corners} padding="l">
            <Row spacingV={isUpload ? undefined : "ml"}>
              <Column span={12}>
                <ProjectEntryMethodToggle
                  // RadioButtonGroup moves its selection optimistically and only re-syncs when the
                  // value prop changes - which it does not when a switch is cancelled. Remounting
                  // puts the dot back on the method actually in use.
                  key={entryMethodResetKey}
                  value={entryMethod}
                  disabled={isReadOnly || isPartiallyCreated}
                  onChange={handleEntryMethodChange}
                />
              </Column>
            </Row>

            {/* A bulk upload carries its landholdings in the file's own columns. */}
            {!isUpload && (
              <Row>
                <Column span={12}>
                  <RadioButtonGroup
                    label={LANDHOLDING_QUESTION}
                    data={LANDHOLDING_RADIO_DATA}
                    value={isLandholding ? "yes" : "no"}
                    direction="horizontal"
                    disabled={isReadOnly}
                    onChange={(e) => handleLandholdingChoiceChange(e.target.value === "yes")}
                  />
                </Column>
              </Row>
            )}
          </Card>
        </Column>
      </Row>

      {isLandholding && !isUpload && (
        <Row spacingV="ml">
          <Column span={12}>
            <Card corners={corners} padding="l">
              <Row spacingV="ml">
                <Column span={12}>
                  <h2>Landholding details</h2>
                </Column>
              </Row>
              <LandholdingDetailsFields
                value={landholding}
                onChange={handleLandholdingChange}
                countryOptions={countryOptions}
                corners={corners}
                errors={landholdingErrors}
                heading={null}
                disabled={isReadOnly}
              />
            </Card>
          </Column>
        </Row>
      )}

      <Row spacingV="ml">
        <Column span={12}>
          <Card corners={corners} padding="l">
            {!isAwaitingUpload && (
              <Row spacingV="ml">
                <Column span={12}>
                  <h2>Project details</h2>
                </Column>
              </Row>
            )}

            {entryMethod === "manual" &&
              rows.map((row, index) => (
                <ManualProjectEntryForm
                  key={row.key}
                  value={row}
                  heading={rows.length > 1 ? `Project ${index + 1}` : undefined}
                  projectTypeOptions={getProjectTypeOptions(row.standardUuid)}
                  standardOptions={getStandardOptions(row.projectTypeUuid)}
                  verifierOptions={verifierOptions}
                  countryOptions={countryOptions}
                  errors={rowErrors[row.key]}
                  corners={corners}
                  isLoading={isSubmitting}
                  disabled={isReadOnly}
                  nextLabel={nextLabel}
                  showActions={index === rows.length - 1}
                  onRemove={rows.length > 1 && !isReadOnly ? () => handleRemoveProject(row.key) : undefined}
                  onChange={handleRowChange}
                  onAddProject={handleAddProject}
                  onNext={handleNext}
                />
              ))}

            {isAwaitingUpload && (
              <Row>
                <Column span={12}>
                  <div className="ProjectDetailsStep_upload">
                    <h2>Upload project</h2>
                    <div className="ProjectDetailsStep_uploadActions">
                      <Button
                        text="Upload file"
                        icon={<UploadLightIcon />}
                        iconPosition="left"
                        corners={corners}
                        onClick={handleOpenUploadModal}
                      />
                      <Button
                        variant="secondary"
                        text="Download template"
                        icon={<DownloadDarkIcon />}
                        iconPosition="left"
                        corners={corners}
                        onClick={handleDownloadTemplate}
                      />
                    </div>
                  </div>
                </Column>
              </Row>
            )}

            {entryMethod === "upload" && rows.length > 0 && (
              <>
                <ParsedProjectsTable
                  rows={rows}
                  rowErrors={uploadRowErrors}
                  isLoading={isSubmitting}
                  loadingMessage={submittingMessage}
                  disabled={isReadOnly}
                  corners={corners}
                  variant={variant}
                  onReplaceFile={handleOpenUploadModal}
                />
                <Row>
                  <Column span={12}>
                    <div className="ProjectDetailsStep_uploadNext">
                      <Button
                        text={nextLabel}
                        icon={<ArrowRoundRightLightIcon />}
                        corners={corners}
                        isLoading={isSubmitting}
                        onClick={handleNext}
                      />
                    </div>
                  </Column>
                </Row>
              </>
            )}
          </Card>
        </Column>
      </Row>

      <BulkUploadModal
        show={showUploadModal}
        isParsing={isParsing}
        parseError={parseError}
        showOutOfDateTemplateHint={showOutOfDateTemplateHint}
        onClose={handleCancelUpload}
        onCancel={handleCancelUpload}
        onDownloadTemplate={handleDownloadTemplate}
        onFileSelected={handleFileSelected}
        onUpload={handleUpload}
      />

      <SwitchingDataEntryWarningModal
        show={showSwitchModal}
        currentMethod={entryMethod}
        projectCount={switchProjectCount}
        onDiscardAndSwitch={handleConfirmDiscard}
        onKeepEntries={handleKeepEntries}
      />
    </div>
  );
};
